import { useState, useRef } from 'react'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import { useUploadThumbnail, useDeleteThumbnail } from '@/hooks/useComponents'

export interface ThumbnailUploadProps {
  componentId: string
  currentThumbnail?: {
    id: string
    filename: string
    url: string
  } | null
}

export const ThumbnailUpload: React.FC<ThumbnailUploadProps> = ({
  componentId,
  currentThumbnail
}) => {
  const [preview, setPreview] = useState<string | null>(currentThumbnail?.url || null)
  const [isDragging, setIsDragging] = useState(false)
  const [removeButtonHover, setRemoveButtonHover] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useUploadThumbnail()
  const deleteMutation = useDeleteThumbnail()

  const handleFileSelect = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (JPG, PNG, WebP)')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB')
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // Upload to server
    try {
      await uploadMutation.mutateAsync({ componentId, file })
    } catch (error) {
      setPreview(currentThumbnail?.url || null)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleRemove = async () => {
    if (window.confirm('Remove thumbnail?')) {
      try {
        await deleteMutation.mutateAsync(componentId)
        setPreview(null)
      } catch (error) {
        // Error already shown via toast
      }
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{
        display: 'block',
        fontSize: 14,
        fontWeight: 500,
        color: '#374151'
      }}>
        Component Thumbnail
      </label>

      {preview ? (
        // Show preview
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={preview}
            alt="Component thumbnail"
            style={{
              width: 128,
              height: 128,
              objectFit: 'cover',
              borderRadius: 4,
              border: '1px solid #d1d5db'
            }}
          />
          <button
            onClick={handleRemove}
            disabled={deleteMutation.isPending}
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              padding: 4,
              backgroundColor: removeButtonHover ? '#dc2626' : '#ef4444',
              color: '#ffffff',
              borderRadius: '50%',
              border: 'none',
              cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: deleteMutation.isPending ? 0.5 : 1,
              transition: 'background-color 150ms',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Remove thumbnail"
            onMouseEnter={() => setRemoveButtonHover(true)}
            onMouseLeave={() => setRemoveButtonHover(false)}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
      ) : (
        // Show compact upload area
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            border: `2px dashed ${isDragging ? '#60a5fa' : '#d1d5db'}`,
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'all 150ms',
            backgroundColor: isDragging ? '#eff6ff' : 'transparent'
          }}
          onMouseEnter={(e) => {
            if (!isDragging) {
              e.currentTarget.style.borderColor = '#9ca3af';
              e.currentTarget.style.backgroundColor = '#f9fafb';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragging) {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          {uploadMutation.isPending ? (
            <>
              <Upload style={{
                width: 24,
                height: 24,
                color: '#6b7280',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }} />
              <span style={{ fontSize: 13, color: '#6b7280' }}>Uploading...</span>
            </>
          ) : (
            <>
              <ImageIcon style={{ width: 24, height: 24, color: '#6b7280' }} />
              <div>
                <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: '#374151' }}>
                  Drop image or click to upload
                </p>
                <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>
                  JPG, PNG, WebP (max 5MB)
                </p>
              </div>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}
